import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import axios from "axios";
import { VolunteersHighlightService } from "../../services/volunteersHighlightService";
import { VolunteersHighlightValidator } from "../../validators/volunteersHighlightValidator";
import { getErrorMessage } from "../../utils/errorUtils";
import {
	ApiResponse,
	getDefaultVolunteersHighlightFormData,
	OperationStatus,
	toVolunteersHighlightEditFormData,
	UpdateVolunteersHighlightLocalizable,
	UpdateVolunteersHighlightRequest,
	VolunteersHighlightDetail,
	VolunteersHighlightEditFormData,
	VolunteersHighlightFormData,
	VolunteersHighlightListItem,
} from "./constants";

export const VOLUNTEERS_HIGHLIGHT_LIST_PATH = "/volunteers-highlights";

const NOT_FOUND_MESSAGE = "Öne çıkan içerik bulunamadı.";

interface VolunteersHighlightContextState {
	// Core Domain Data
	volunteersHighlightList: VolunteersHighlightListItem[];
	volunteersHighlightDetail: VolunteersHighlightDetail | null;
	volunteersHighlightForm: VolunteersHighlightFormData;
	volunteersHighlightEditForm: VolunteersHighlightEditFormData | null;
	operationStatus: OperationStatus;

	// Setters
	setVolunteersHighlightForm: React.Dispatch<React.SetStateAction<VolunteersHighlightFormData>>;
	setVolunteersHighlightEditForm: React.Dispatch<React.SetStateAction<VolunteersHighlightEditFormData | null>>;
	clearVolunteersHighlightFormData: () => void;
	clearVolunteersHighlightList: () => void;

	// Actions
	getVolunteersHighlightList: () => Promise<void>;
	getVolunteersHighlightDetails: (id: string) => Promise<void>;
	addVolunteersHighlight: () => Promise<void>;
	updateVolunteersHighlight: () => Promise<void>;
	removeVolunteersHighlight: (id: string) => Promise<void>;
}

interface VolunteersHighlightProviderProps {
	children: React.ReactNode;
}

const VolunteersHighlightContext = createContext<VolunteersHighlightContextState | undefined>(undefined);

export const useVolunteersHighlightContext = () => {
	const context = useContext(VolunteersHighlightContext);
	if (!context) {
		throw new Error("useVolunteersHighlightContext must be used within a VolunteersHighlightContextProvider");
	}
	return context;
};

const getResponseErrorMessage = (response: ApiResponse<unknown>) => {
	const error = response.error;
	if (typeof error === "string") {
		return error;
	}
	return error?.message || "Beklenmedik bir hata oluştu.";
};

const isNotFoundError = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 404;

const showError = (error: unknown) => {
	console.error(error);
	message.error(isNotFoundError(error) ? NOT_FOUND_MESSAGE : getErrorMessage(error));
};

/* -------------- Build Update Request (Only Changed Fields) -------------- */

export const buildVolunteersHighlightUpdateRequest = (
	current: VolunteersHighlightEditFormData,
	original: VolunteersHighlightEditFormData
): UpdateVolunteersHighlightRequest => {
	const request: UpdateVolunteersHighlightRequest = { highlightId: current.highlightId };

	if (current.isActive !== original.isActive) {
		request.isActive = current.isActive;
	}
	if (current.displayOrder !== original.displayOrder) {
		request.displayOrder = current.displayOrder ?? null;
	}

	const changedLocalizables = current.volunteersHighlightLocalizable.flatMap(item => {
		const before = original.volunteersHighlightLocalizable.find(o => o.culture === item.culture);
		const changed: UpdateVolunteersHighlightLocalizable = {
			highlightLocalizableId: item.highlightLocalizableId,
			culture: item.culture,
		};
		let hasChange = false;

		if (item.title.trim() !== (before?.title ?? "").trim()) {
			changed.title = item.title;
			hasChange = true;
		}
		if (item.description.trim() !== (before?.description ?? "").trim()) {
			changed.description = item.description;
			hasChange = true;
		}
		if (item.image) {
			changed.image = item.image;
			hasChange = true;
		}

		return hasChange ? [changed] : [];
	});

	if (changedLocalizables.length > 0) {
		request.volunteersHighlightLocalizable = changedLocalizables;
	}

	return request;
};

export const VolunteersHighlightContextProvider: React.FC<VolunteersHighlightProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [volunteersHighlightList, setVolunteersHighlightList] = useState<VolunteersHighlightListItem[]>([]);
	const [volunteersHighlightDetail, setVolunteersHighlightDetail] = useState<VolunteersHighlightDetail | null>(null);
	const [volunteersHighlightForm, setVolunteersHighlightForm] = useState<VolunteersHighlightFormData>(getDefaultVolunteersHighlightFormData);
	const [volunteersHighlightEditForm, setVolunteersHighlightEditForm] = useState<VolunteersHighlightEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	// The loading state only disables the buttons after a re-render, so a fast double click could still send a
	// second request; this ref blocks overlapping add/update/delete calls synchronously.
	const isMutatingRef = useRef(false);

	/* -------------- Clear helpers -------------- */

	const clearVolunteersHighlightFormData = useCallback(() => {
		setVolunteersHighlightForm(getDefaultVolunteersHighlightFormData());
		setVolunteersHighlightEditForm(null);
		setVolunteersHighlightDetail(null);
	}, []);

	const clearVolunteersHighlightList = useCallback(() => {
		setVolunteersHighlightList([]);
	}, []);

	/* -------------- Actions -------------- */

	const getVolunteersHighlightList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersHighlightService.getVolunteersHighlightList();

			if (response.isSuccessful) {
				setVolunteersHighlightList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getVolunteersHighlightDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersHighlightService.getVolunteersHighlightDetails(id);

			if (response.isSuccessful) {
				setVolunteersHighlightDetail(response.result);
				setVolunteersHighlightEditForm(toVolunteersHighlightEditFormData(response.result));
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				navigate(VOLUNTEERS_HIGHLIGHT_LIST_PATH);
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [navigate]);

	const addVolunteersHighlight = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		const validationError = VolunteersHighlightValidator.validateAddVolunteersHighlight(volunteersHighlightForm);
		if (validationError) {
			message.info(validationError);
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "add" });

		try {
			const response = await VolunteersHighlightService.addVolunteersHighlight(volunteersHighlightForm);

			if (response.isSuccessful) {
				message.success("Öne çıkan içerik başarıyla eklendi.");
				setVolunteersHighlightForm(getDefaultVolunteersHighlightFormData());
				navigate(VOLUNTEERS_HIGHLIGHT_LIST_PATH);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersHighlightForm, navigate]);

	const updateVolunteersHighlight = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}
		if (!volunteersHighlightEditForm || !volunteersHighlightDetail) {
			message.error(NOT_FOUND_MESSAGE);
			return;
		}

		const updateRequest = buildVolunteersHighlightUpdateRequest(
			volunteersHighlightEditForm,
			toVolunteersHighlightEditFormData(volunteersHighlightDetail)
		);

		const validationError = VolunteersHighlightValidator.validateUpdateVolunteersHighlight(updateRequest);
		if (validationError) {
			message.info(validationError);
			return;
		}

		if (Object.keys(updateRequest).length === 1) {
			message.info("Herhangi bir değişiklik yapılmadı.");
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "update" });

		try {
			const response = await VolunteersHighlightService.updateVolunteersHighlight(updateRequest);

			if (response.isSuccessful) {
				message.success("Öne çıkan içerik başarıyla güncellendi.");
				clearVolunteersHighlightFormData();
				navigate(VOLUNTEERS_HIGHLIGHT_LIST_PATH);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				navigate(VOLUNTEERS_HIGHLIGHT_LIST_PATH);
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersHighlightEditForm, volunteersHighlightDetail, navigate, clearVolunteersHighlightFormData]);

	const removeVolunteersHighlight = useCallback(async (id: string) => {
		if (isMutatingRef.current) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await VolunteersHighlightService.removeVolunteersHighlight(id);

			if (response.isSuccessful) {
				message.success("Öne çıkan içerik başarıyla silindi.");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}

		// The backend re-numbers the remaining items, so the list is refreshed after every attempt.
		await getVolunteersHighlightList();
	}, [getVolunteersHighlightList]);

	const contextValue = useMemo<VolunteersHighlightContextState>(
		() => ({
			volunteersHighlightList,
			volunteersHighlightDetail,
			volunteersHighlightForm,
			volunteersHighlightEditForm,
			operationStatus,
			setVolunteersHighlightForm,
			setVolunteersHighlightEditForm,
			clearVolunteersHighlightFormData,
			clearVolunteersHighlightList,
			getVolunteersHighlightList,
			getVolunteersHighlightDetails,
			addVolunteersHighlight,
			updateVolunteersHighlight,
			removeVolunteersHighlight,
		}),
		[
			volunteersHighlightList,
			volunteersHighlightDetail,
			volunteersHighlightForm,
			volunteersHighlightEditForm,
			operationStatus,
			clearVolunteersHighlightFormData,
			clearVolunteersHighlightList,
			getVolunteersHighlightList,
			getVolunteersHighlightDetails,
			addVolunteersHighlight,
			updateVolunteersHighlight,
			removeVolunteersHighlight,
		]
	);

	return <VolunteersHighlightContext.Provider value={contextValue}>{children}</VolunteersHighlightContext.Provider>;
};
